import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';

interface PolymarketChartData {
  time: string;
  yes: number;
  no: number;
  timeNum: number;
  homeLabel: string;
  awayLabel: string;
}

interface Event {
  homeTeam: string;
  awayTeam: string;
  isLive?: boolean;
}

interface PolymarketChartProps {
  event: Event;
  polymarketChartData: PolymarketChartData[];
}

export default function PolymarketChart({
  event,
  polymarketChartData,
}: PolymarketChartProps) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">POLYMARKET PRICE TRACKER</h2>
          <p className="text-sm text-gray-400 mt-1">
            Prediction market prices - Updates every {event.isLive ? '1 minute' : '10 minutes'}
          </p>
        </div>
        <div className="flex gap-2">
          <span className="px-3 py-1 text-xs rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
            {polymarketChartData.length > 0 ? polymarketChartData[0].homeLabel : event.homeTeam}
          </span>
          <span className="px-3 py-1 text-xs rounded bg-pink-500/20 text-pink-400 border border-pink-500/30">
            {polymarketChartData.length > 0 ? polymarketChartData[0].awayLabel : event.awayTeam}
          </span>
        </div>
      </div>
      <div className="h-64">
        {polymarketChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={polymarketChartData}>
              <XAxis
                dataKey="time"
                stroke="#6b7280"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                stroke="#6b7280"
                tick={{ fontSize: 11 }}
                domain={[0, 1]}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                label={{ value: 'Probability', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a2e',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => {
                  const label = name === 'yes'
                    ? polymarketChartData[0]?.homeLabel
                    : polymarketChartData[0]?.awayLabel;
                  return [`${(value * 100).toFixed(1)}%`, label];
                }}
              />
              <Legend />
              <ReferenceLine y={0.5} stroke="#374151" strokeDasharray="3 3" label={{ value: '50%', fill: '#6b7280', fontSize: 10 }} />
              <Line
                type="monotone"
                dataKey="yes"
                name={polymarketChartData[0]?.homeLabel || event.homeTeam}
                stroke="#a855f7"
                strokeWidth={3}
                dot={{ r: 2, fill: '#a855f7' }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="no"
                name={polymarketChartData[0]?.awayLabel || event.awayTeam}
                stroke="#ec4899"
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={{ r: 2, fill: '#ec4899' }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-4xl mb-2">📊</div>
              <div>No Polymarket price history available</div>
              <div className="text-xs text-gray-500 mt-1">Polymarket data will appear once collected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
